/**
 * The first-run rail's state machine — pure, so it can be tested without a
 * DOM (see ./machine.test.ts).
 *
 * The scripted arc, with every optional beat present:
 *
 *   welcome -> problem -> inputs -> choose -> promise -> replay -> reveal
 *           -> proof -> ask -> repeat -> closing
 *
 * and, for a demo authored before the problem beat existed, exactly the arc it
 * has always played:
 *
 *   welcome -> what -> inputs -> choose -> promise -> replay -> reveal -> ask
 *           -> closing
 *
 * `problem`/`what` and `inputs` are the ORIENT beats, and they exist because of
 * one verdict from a real walkthrough: "the user doesn't know anything about
 * the product or the data we are showing them — context must be set BEFORE the
 * demo or data is shown." The arc used to open on a welcome line and go
 * straight to a menu of workflows, which asked a prospect to choose between
 * three things they had no way to tell apart, run against files they had never
 * been shown.
 *
 * WHICH ORIENT BEAT OPENS is the redesign's one structural rule. `what`
 * explains the product, which is the seller's subject; `problem` names the
 * prospect's own recurring pain and then releases it with what they're about to
 * get, which is theirs. When a demo authors `pain_line`, `problem` plays and
 * `what` is skipped ENTIRELY — the product explanation is demoted out of the
 * rail (it lives on the sign-in page now), and the fiction disclosure it used
 * to carry moves into `inputs`, where the invented files are actually on
 * screen. The two beats never coexist: one opening, not two.
 *
 * `proof` is the TRUST beat, straight after the reveal and before the ask —
 * the permission ladder's "can I trust it?" rung, which the arc used to skip
 * entirely (it lived only in the second-visit tour, where nothing was
 * clickable). The prospect has just read numbers a committee would have to
 * defend; this beat invites them to click one and watch it name its source
 * file. The rail is a bottom panel with no scrim, so the page behind it is
 * genuinely clickable — the invitation is real, not narrated. The beat is
 * platform-gated (`payload.proof`) so demos authored before it keep their
 * arc byte-for-byte, and it additionally requires a reveal: with no
 * deliverable on screen there is nothing to challenge.
 *
 * `repeat` is the closing move, between `proof`/`ask` and the exit strip: the
 * run the prospect just watched becomes a standing job with an owner, an
 * output and a cadence. The arc used to end on what one run produced; it now
 * ends on the thing that is actually being sold, which is that it keeps
 * happening.
 *
 * States with nothing to show are dropped from the timeline entirely — a demo
 * whose payload carries no `ask` never renders an empty composer moment, and a
 * demo authored before `context`/`files`/`repeat` existed simply keeps the arc
 * it had — so the rail's whole shape is decided once, up front, by
 * `buildTimeline`. `closing` is always last and always present: it is the exit
 * strip (the shareable summary + "See how it's made"), reachable from any state
 * via skip.
 */

import { paceDurations } from "../pacing";
import type { FirstRunPayload, FirstRunRecordedStep } from "./payload";

export type FirstRunState =
  | "welcome"
  | "problem"
  | "what"
  | "inputs"
  | "choose"
  | "promise"
  | "replay"
  | "reveal"
  | "proof"
  | "ask"
  | "repeat"
  | "closing";

/** Canonical order. `buildTimeline` filters this; it never reorders it. */
export const FIRST_RUN_ORDER: readonly FirstRunState[] = [
  "welcome",
  "problem",
  "what",
  "inputs",
  "choose",
  "promise",
  "replay",
  "reveal",
  "proof",
  "ask",
  "repeat",
  "closing",
] as const;

/**
 * The states this demo's payload can actually fill, in order.
 *
 * `hasWelcome` is separate from the payload because the welcome line is the
 * demo's EXISTING `me.demo.welcome` (already rendered server-side), not a new
 * first-run field — the rail reuses it rather than asking the platform to
 * author the same sentence twice.
 */
export function buildTimeline(
  payload: FirstRunPayload,
  opts: { hasWelcome: boolean },
): FirstRunState[] {
  // The opening beat. A pain line is what earns it, and its presence is also
  // what RETIRES `what` — see the header: one opening, not two.
  const hasProblem = (payload.context?.pain_line || "").length > 0;
  const present: Record<FirstRunState, boolean> = {
    welcome: opts.hasWelcome,
    problem: hasProblem,
    // The orient beats are strictly opt-in: a payload that predates them (or a
    // control plane that authored neither line) degrades to exactly the arc
    // this rail shipped with. `client_name` alone does not earn the state —
    // it is spoken by `inputs` and `reveal`, and a screen that says only
    // "Acme Capital" orients nobody.
    what:
      !hasProblem &&
      (payload.context?.product_line || payload.context?.fiction_line || "").length > 0,
    inputs: payload.files.length > 0,
    choose: payload.cards.length > 0,
    promise: payload.promise !== null,
    replay: payload.replay.length > 0,
    reveal: payload.reveal !== null,
    // The trust beat needs BOTH a platform mark (so pre-proof demos keep
    // their arc unchanged) and a reveal (so there is a page of figures on
    // screen to challenge when it plays).
    proof: payload.proof && payload.reveal !== null,
    ask: payload.ask !== null,
    repeat: payload.repeat !== null,
    closing: true,
  };
  return FIRST_RUN_ORDER.filter((state) => present[state]);
}

/**
 * Which beat speaks `expect_line` — the release of the tension the opening set.
 *
 * It belongs to `problem`, immediately under the pain it answers. Only when
 * there is no problem beat does it stay where it has always been, under the
 * welcome line, so a demo authored before `pain_line` loses nothing.
 *
 * Pure, and here rather than inside the rail, because "which screen says this
 * sentence" is a question about the arc's shape and is worth a test.
 */
export function expectLineHost(timeline: readonly FirstRunState[]): "problem" | "welcome" {
  return timeline.includes("problem") ? "problem" : "welcome";
}

/**
 * Which beat discloses that the client is invented.
 *
 * `what` says it when `what` plays. When the problem beat replaced it, the
 * disclosure moves to `inputs` — the screen where the invented files are
 * actually listed, which is the better place for it anyway. It is never said
 * twice, and never not at all.
 */
export function fictionLineHost(timeline: readonly FirstRunState[]): "what" | "inputs" {
  return timeline.includes("what") ? "what" : "inputs";
}

/** The state the rail opens on, or `null` when the timeline is exit-only. */
export function firstState(timeline: readonly FirstRunState[]): FirstRunState | null {
  const first = timeline[0];
  // A timeline of nothing but `closing` means every scripted state was empty.
  // The payload guard already refuses that case; belt-and-braces here so the
  // rail can never open straight onto its own exit.
  if (!first || first === "closing") return null;
  return first;
}

/**
 * The state after `current`, or `null` when the arc is over (dismiss the rail).
 *
 * An unknown `current` (stale state after a payload change mid-session)
 * resolves to the timeline's first state rather than throwing.
 */
export function nextState(
  timeline: readonly FirstRunState[],
  current: FirstRunState,
): FirstRunState | null {
  const idx = timeline.indexOf(current);
  if (idx === -1) return timeline[0] ?? null;
  return timeline[idx + 1] ?? null;
}

/**
 * The state before `current`, or `null` at the arc's start (no back).
 *
 * The arc was forward-only by design — a march. A real walkthrough asked the
 * obvious question ("no back button?"): a prospect who wants to re-read the
 * previous beat should not have to replay the whole intro to get it. Back is
 * a small ghost affordance, never the primary action; an unknown `current`
 * behaves like the start.
 */
export function prevState(
  timeline: readonly FirstRunState[],
  current: FirstRunState,
): FirstRunState | null {
  const idx = timeline.indexOf(current);
  if (idx <= 0) return null;
  return timeline[idx - 1] ?? null;
}

/**
 * Where "skip" goes from `current`.
 *
 * Skipping never throws the prospect out — it lands on the closing strip, so
 * they still get the receipt and the handoff into the product tour. Skipping
 * FROM the closing strip is the one exit that dismisses (`null`).
 */
export function skipTarget(
  timeline: readonly FirstRunState[],
  current: FirstRunState,
): FirstRunState | null {
  if (current === "closing") return null;
  return timeline.includes("closing") ? "closing" : null;
}

/** 1-based position of `current` among the scripted states (closing excluded),
 *  for the rail's step dots. Returns 0 for the closing strip. */
export function stepIndex(timeline: readonly FirstRunState[], current: FirstRunState): number {
  if (current === "closing") return 0;
  return timeline.filter((s) => s !== "closing").indexOf(current) + 1;
}

/** How many scripted states this arc has (closing excluded). */
export function stepCount(timeline: readonly FirstRunState[]): number {
  return timeline.filter((s) => s !== "closing").length;
}

// ── whose run the replay beat plays ──────────────────────────────────────────

/**
 * The replay the `replay` beat plays: the chosen card's own run when the
 * platform sent one, else the whole provisioning feed (a payload from before
 * per-card replays, or an arc with no card picked).
 */
export function replayFor(
  payload: Pick<FirstRunPayload, "replay">,
  chosen: { replay: FirstRunPayload["replay"] | null } | null,
): FirstRunPayload["replay"] {
  return chosen?.replay ?? payload.replay;
}

// ── the ask beat's hand-off ──────────────────────────────────────────────────

/** A screen rectangle, as getBoundingClientRect answers. */
export type ScreenRect = { left: number; top: number; right: number; bottom: number };

/**
 * How the ask beat hands its question to the assistant.
 *
 *  - `staged`: the question flies into a composer the prospect can SEE, and
 *    they send it — from the rail or the composer. Three honest clicks.
 *  - `direct`: there is no composer to see. The rail opens the agent panel
 *    and sends the question through it, and its copy promises no box.
 *
 * Regression: Oxera, 2026-10-13. Below ~700px the agent panel was collapsed,
 * yet the beat said the question was "in the composer on your right — the
 * box is lit up", and its Send did nothing: no composer was mounted. The
 * same holds for a closed panel, the narrow-window overlay (which the rail
 * itself covers), a composer hidden behind the Comments tab, and a docked
 * one the rail sits on.
 */
export type AskHandoff = "staged" | "direct";

/** Share of the composer the rail may cover before the box no longer counts
 *  as visible. */
export const ASK_COVER_LIMIT = 0.2;

function area(r: ScreenRect): number {
  return Math.max(0, r.right - r.left) * Math.max(0, r.bottom - r.top);
}

function overlap(a: ScreenRect, b: ScreenRect): number {
  return area({
    left: Math.max(a.left, b.left),
    top: Math.max(a.top, b.top),
    right: Math.min(a.right, b.right),
    bottom: Math.min(a.bottom, b.bottom),
  });
}

/** Staged only when the composer is on screen, of real size, and not under
 *  the rail; direct otherwise. Pure — the rail measures, this decides. */
export function askHandoff(
  composer: ScreenRect | null,
  rail: ScreenRect | null,
  viewport: { width: number; height: number },
): AskHandoff {
  if (!composer) return "direct";
  const size = area(composer);
  // display:none (a hidden tab) measures 0×0; a sliver is not a box.
  if (composer.right - composer.left < 40 || composer.bottom - composer.top < 10) return "direct";
  const screen = { left: 0, top: 0, right: viewport.width, bottom: viewport.height };
  if (overlap(composer, screen) < size * 0.5) return "direct";
  if (rail && overlap(composer, rail) > size * ASK_COVER_LIMIT) return "direct";
  return "staged";
}

/** What the ask beat says, which forward action it offers, and whether it
 *  shrinks to one line so the answer playing in the panel stays in view. */
export type AskBeat = {
  line: string;
  action: "try" | "send" | "keep" | null;
  compact: boolean;
};

export const ASK_INTRO = "It answers from the same files, and shows its work.";

/**
 * The ask beat, per hand-off.
 *
 * Only the STAGED path may talk about the composer: it is the one path where
 * a lit box is on screen. The direct path says what is actually happening —
 * the assistant panel opening, the question being asked there — and, once it
 * is sent, shrinks to one line: on a narrow screen a full-height panel would
 * sit on top of the very answer it is telling them to watch.
 *
 * `stalled`: a direct hand-off nobody picked up (no panel opened on this
 * page). The beat says so and lets them go on, rather than waiting forever on
 * a send that will not come.
 */
export function askBeat(s: {
  handoff: AskHandoff | null;
  tried: boolean;
  sent: boolean;
  stalled: boolean;
}): AskBeat {
  if (!s.tried || s.handoff === null) return { line: ASK_INTRO, action: "try", compact: false };
  if (s.handoff === "staged") {
    return s.sent
      ? {
          line: "Watch it answer on the right — every step you see really ran. When you've read it, keep going.",
          action: "keep",
          compact: false,
        }
      : {
          line: "It's in the composer on your right — the box is lit up. Send it from either side, or change the question to anything you like first.",
          action: "send",
          compact: false,
        };
  }
  if (s.sent) {
    return {
      line: "It's answering in the assistant panel — every step you see really ran.",
      action: "keep",
      compact: true,
    };
  }
  if (s.stalled) {
    return {
      line: "The assistant panel didn't open here. Open it from the chat button at the top of the page and ask it there — or keep going.",
      action: "keep",
      compact: false,
    };
  }
  return { line: "Asking it now — the assistant panel is opening.", action: null, compact: true };
}

// ── replay pacing ────────────────────────────────────────────────────────────

/** Fastest cadence we'll play the provisioning log at (4 lines/sec). */
export const REPLAY_MIN_INTERVAL_MS = 250;
/** Slowest cadence (2 lines/sec) — below this the arc stops feeling like a run. */
export const REPLAY_MAX_INTERVAL_MS = 500;
/** The replay's whole time budget inside a ninety-second arc. */
export const REPLAY_BUDGET_MS = 15_000;

/**
 * Milliseconds between replay lines.
 *
 * The payload's `t` values are the REAL run's offsets — minutes, sometimes —
 * so they order the log but never time it. Instead the rail spends at most
 * REPLAY_BUDGET_MS on the whole log, and always inside 2–4 lines/sec: short
 * logs breathe at the slow end, long ones run at the fast end and simply take
 * the full budget.
 */
export function replayIntervalMs(lineCount: number): number {
  if (lineCount <= 1) return REPLAY_MAX_INTERVAL_MS;
  const fitBudget = REPLAY_BUDGET_MS / lineCount;
  return Math.min(REPLAY_MAX_INTERVAL_MS, Math.max(REPLAY_MIN_INTERVAL_MS, fitBudget));
}

// ── recorded live-build pacing ───────────────────────────────────────────────

/** Floor for a recorded step's on-screen time. A step the server measured at
 *  4ms (the cache probe) still needs long enough to be read. */
export const RECORDED_MIN_STEP_MS = 220;
/** Ceiling for one step. A genuinely slow compile is reported by its LABEL
 *  ("8.4s"), which stays the server's real number — it does not also need to
 *  cost the prospect eight silent seconds. */
export const RECORDED_MAX_STEP_MS = 2600;
/** Whole-recording budget, matching the recorded replay's own. */
export const RECORDED_BUDGET_MS = 15_000;

/**
 * Per-step delays for replaying a recorded live build.
 *
 * Returns one delay per step: how long to wait BEFORE revealing that step.
 *
 * The honesty line is the same one the live build already draws. The DURATION
 * printed beside each step is, and stays, the server's own measurement of work
 * that actually ran. What this function produces is a READING PACE — derived
 * from those measurements so a slow step still feels slower than a fast one,
 * but clamped per step and scaled to a budget so a four-minute run doesn't
 * become a four-minute rail. Nothing here is ever shown as a number.
 *
 * Reduced motion skips this entirely (the rail lands the log complete).
 *
 * The clamp-and-fit itself lives in ../pacing.ts, shared with the recorded
 * conversation replay — same problem, one implementation.
 */
export function recordedStepDelaysMs(steps: readonly FirstRunRecordedStep[]): number[] {
  return paceDurations(
    steps.map((step) => step.ms),
    {
      minMs: RECORDED_MIN_STEP_MS,
      maxMs: RECORDED_MAX_STEP_MS,
      budgetMs: RECORDED_BUDGET_MS,
    },
  );
}
